import { http } from 'App/http';
import store from '../store'

function _normalizeValueForFormData(data)
{
    switch (typeof data) {
        case 'undefined':
            return '';
        case 'boolean':
            return data ? 1 : 0;
        default:
            return data === null ? '' : data;
    }
}

function _buildFormData(formData, data, parentKey) {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
        Object.keys(data).forEach(key => {
            _buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
        });
    } else if ((parentKey[0] !== '_' || parentKey === '_method') && typeof data !== 'function') {
        formData.append(parentKey, _normalizeValueForFormData(data));
    }
}

export function objectToFormData(data) {
    const formData = new FormData();

    _buildFormData(formData, data);

    return formData;
}

export function getParameterByName(name, url = null) {
    if (!url) {
        url = window.location.href;
    }

    name = name.replace(/[\[\]]/g, '\\$&');

    var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
        results = regex.exec(url);

    if (!results) {
        return null;
    }

    if (!results[2]) {
        return '';
    }

    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

class FileUploaderService {
    async store(file, options = {}) {
        const response = await http.post('/app/signed-storage-url', {
            file_name: file.name,
            content_type: options.contentType || file.type,
            expires: options.expires || '',
        }, {
            baseURL: options.baseURL || null,
            headers: options.headers || {}
        });

        let headers = response.data.headers;
        const uploadUrl = response.data.url;
        const cancelToken = options.cancelToken || '';

        if ('Host' in headers) {
            delete headers.Host;
        }

        if (typeof options.progress === 'undefined') {
            options.progress = () => {};
        }

        await http.put(uploadUrl, file, {
            cancelToken: cancelToken,
            headers: headers,
            onUploadProgress: (progressEvent) => {
                options.progress(progressEvent.loaded / progressEvent.total);
            }
        })

        response.data.extension = file.name.split('.').pop()

        return response.data;
    }
}

export const FileUploader = new FileUploaderService();

export function randomId() {
    return Math.random().toString(36).substring(2);
}

export function getQueryParam(name = null) {
    let params = {};
    let queryStringSplit = window.location.search.replace(/^\?/, '').split('&');

    queryStringSplit.forEach(queryParam => {
        let [key, value] = queryParam.split('=');
        params[key] = decodeURIComponent(value);
    });

    if (name) {
        return params[name];
    }

    return params;
}

export function insertQueryParam(key, value) {
    if ('URLSearchParams' in window) {
        var searchParams = new URLSearchParams(window.location.search)
        searchParams.set(key, value);
        var newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
        history.pushState(null, '', newRelativePathQuery);
    }
}

export function minutesToHumanFormat(minutes) {
    let hours = Math.floor(minutes / 60);
    minutes = minutes % 60;

    if (hours == 1 && minutes == 0) {
        return `1 ${trans('online_booking.hour')}`;
    }

    if (hours == 1 && minutes > 0) {
        return `1 ${trans('online_booking.hour')} ${trans('global.and')} ${minutes} ${trans('online_booking.minutes')}`;
    }

    if (hours > 1 && minutes == 0) {
        return `${hours} ${trans('online_booking.hours')}`;
    }

    if (hours > 1 && minutes > 0) {
        return `${hours} ${trans('online_booking.hours')} ${trans('global.and')} ${minutes} ${trans('online_booking.minutes')}`;
    }

    if (minutes > 1) {
        return `${minutes} ${trans('online_booking.minutes')}`;
    }

    return `1 ${trans('products.minute')}`;
}

export function copyToClipboard(text, toastrMessage = null) {
    if (!text) {
        return;
    }

    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);

    if (toastrMessage) {
        toastr.success(toastrMessage);
    }
}

export function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&'); // $& means the whole matched string
}

export function escapeHtmlCharacters(string) {
    return string.replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
}

class PhoneNumber
{
    constructor(rawNumber, user) {
        this.rawNumber = rawNumber;
        this.user = user;
        this.parse();
    }

    parse(rawNumber = null, user = null) {
        rawNumber = rawNumber || this.rawNumber;
        user = user || this.user;

        if (!user || !user.country) {
            throw new Error('Please provide a user with a "country" attribute.');
        }

        this.rawNumber = rawNumber;
        this.user = user;

        rawNumber = String(rawNumber).replace(/[^\\+0-9]/gi, ' ');
        const defaultCountryCode = Config.get(`countries.${user.country}.phone_contry_code`);
        let countryCode = null;
        let number = null;

        if (rawNumber.substring(0, 2) === '00') {
            // in many countries, two leading zeroes mean it's an international phone number
            rawNumber = rawNumber.replace(/^0+/gi, '');
        }

        if (rawNumber[0] === '0') {
            this.number = rawNumber.replace(/^0+/gi, '');
            this.countryCode = this.number !== '' ? String(defaultCountryCode) : '';
            return;
        }

        if (rawNumber[0] === '+' && rawNumber.indexOf(' ') === -1) {
            throw new Error('Cannot parse a phone number without a space between country code and number.');
        }

        if (rawNumber[0] === '+' && rawNumber.indexOf(' ') > 0) {
            rawNumber = rawNumber.replace(/[^0-9 ]/gi, '');

            [countryCode, number] = rawNumber.split(' ', 2);
        }

        this.number = number || rawNumber.replace(/[^0-9]/gi, '');
        this.countryCode = this.number !== '' ? String(countryCode || '') : '';
    }

    price() {
        if (this.isInternational()) {
            return this.user.sms_price_international
                ? this.user.sms_price_international
                : Config.get(`countries.${this.user.country}.sms_message_price_international`);
        }

        return this.user.sms_price_standard
            ? this.user.sms_price_standard
            : Config.get(`countries.${this.user.country}.sms_message_price`);
    }

    priceFormatted() {
        return Config.get(`countries.${this.user.country}.subscription_currency`) + " " + format_amount(this.price() / 100, 2)
    }

    isInternational() {
        let defaultCountryCode = Config.get(`countries.${this.user.country}.phone_contry_code`);
        return this.countryCode !== '' && this.countryCode !== String(defaultCountryCode);
    }
}

// A simpler version of the PHP's PhoneNumber
export function phoneNumber(rawNumber, user) {
    return new PhoneNumber(rawNumber, user);
}

export function handleRemainingSessionTimeChange(value) {
    if (value === store.getters['auth/sessionExpiringWarningTimeout']) {
        store.dispatch('auth/fetchSessionDetails')
            .then((showSessionExpiring) => {
                if (showSessionExpiring) {
                    setTimeout(() => {
                        swal({
                            title: trans('login.session_expiring_title'),
                            text: trans('login.session_expiring_description'),
                            type: 'warning',
                            confirmButtonText: trans('login.session_extend'),
                        }, (confirmed) => {
                            if (confirmed) {
                                store.dispatch('auth/fetchSessionDetails', { force: true })
                            }
                        });
                    }, 500)
                }
            })
    } else if (value < 0) {
        store.dispatch('auth/fetchSessionDetails')
            .then((showSessionExpiring) => {
                if (showSessionExpiring) {
                    store.commit('auth/setCurrentTimeTimeout', null)
                    window.location.href = '/login'
                } else {
                    swal.close()
                }
            })
    }
}

export const replaceAll = (str, find, replace) => {
    return str.replace(new RegExp(escapeRegExp(find), 'g'), replace);
}

export const numberFormat = (number, decimals = 2, locale = null) => {
    if (typeof number === 'string') {
        number = parseFloat(number);
    }

    if (typeof number !== 'number') {
        throw Error('Invalid number value provided.');
    }

    return new Intl.NumberFormat(
        [locale || moment_locale, 'en-GB'],
        { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
    ).format(number)
}

export const getLongDateFormat = () => {
    // DA long dates have a "d." before the numeric day
    if (moment_locale === 'da') {
        return 'dddd [d.] LL';
    }

    return 'dddd LL';
}

/**
 * Return unique values from an array
 * filter out tags - [tag1], [tag2] etc.
 *
 * The first regex filters for old style tags.
 * The second regex filters for new style tags.
 * Old style = [name]
 * New Style = @name
 */
export const findTagsInContent = (content) => {
    return [
        ...new Set(content.match(/\[(.*?)]/g)),
        ...new Set(content.match((/\B@\w+/g))),
    ]
}

export const appendCustomAttributesToFormData = (formData, customAttributes) => {
    Object.keys(customAttributes).forEach(customAttributeKey => {
        const customAttributeValue = customAttributes[customAttributeKey];

        if (typeof customAttributeValue === 'object') {
            for (const index in customAttributeValue) {
                formData.append(`custom_attributes[${customAttributeKey}][${index}]`, customAttributeValue[index]);
            }
        } else {
            formData.append(`custom_attributes[${customAttributeKey}]`, customAttributeValue);
        }
    })

    return formData;
}

export function saveScrollPosition() {
    sessionStorage.setItem('scrollPosition', window.scrollY);
}

export function restoreScrollPosition() {
    const scrollPosition = sessionStorage.getItem('scrollPosition');
    if (scrollPosition !== null) {
        window.scrollTo({top: parseInt(scrollPosition), behavior: "instant"});
        sessionStorage.removeItem('scrollPosition');
    }
}

export function refreshPage() {
    saveScrollPosition();
    location.reload();
}
